import {auth} from "../../firebaseConfig";
import {addBreadcrumb} from "../../utils/auth";

const RETRY_TIMEOUT = 10 * 1000; // 10 sec
const REFRESH_TIMEOUT = 30 * 60 * 1000; //30 min

class TokenRefresher {
    /**
     * TokenRefresher
     * ---------------
     * A singleton utility that keeps the Firebase ID token of the currently
     * authenticated user fresh throughout the lifetime of the page.
     *
     * Features
     * --------
     * 1. **Automatic refresh**
     *    • Forces a token refresh every 30 minutes (`REFRESH_TIMEOUT`).
     * 2. **Retry logic**
     *    • If the user is not yet available or a refresh fails, schedules a retry
     *      after 10 seconds (`RETRY_TIMEOUT`).
     * 3. **Tab-visibility awareness**
     *    • Whenever the browser tab regains focus (`visibilitychange → visible`),
     *      the class validates the token immediately to avoid using an expired
     *      credential after a long period in the background.
     * 4. **Re-entrancy protection**
     *    • Guards against parallel refresh attempts with `isRefreshing`.
     * 5. **Integration switch**
     *    • Setting `isIntegration = true` disables all refresh activity, allowing
     *      test or integration environments to opt-out.
     * 6. **Breadcrumb logging**
     *    • Logs every significant step (`start`, scheduled refresh, retry, success,
     *      failure, visibility change) both to the console and to an external
     *      monitoring utility via `addBreadcrumb`.
     *
     * Implementation Notes
     * --------------------
     * • Uses `setTimeout` instead of `setInterval` so that each new schedule is
     *   created only after the previous refresh attempt finishes.
     * • The constructor is private to enforce the singleton pattern; obtain the
     *   instance via `TokenRefresher.getInstance()`.
     * • All timers are cleared before re-scheduling to prevent multiple pending
     *   callbacks.
     */

    public isIntegration = false;

    private static instance: TokenRefresher;

    private timeoutId: NodeJS.Timeout | null = null;
    private isRefreshing = false;

    private constructor() {
        this.setupVisibilityHandler();
    }

    public static getInstance(): TokenRefresher {
        if (!TokenRefresher.instance) {
            TokenRefresher.instance = new TokenRefresher();
        }
        return TokenRefresher.instance;
    }

    public async start() {
        this.log('[TokenRefresher] Starting token refresher');
        await this.refresh()
    }

    private async refresh(): Promise<void> {
        if (this.isRefreshing || this.isIntegration) return;

        const user = auth.currentUser;
        if (!user) {
            this.log('User not ready.')
            this.scheduleRetry();
            return;
        }

        this.isRefreshing = true;
        try {
            await user.getIdToken(true);
            this.log('Token has been refreshed successfully.');
            this.scheduleRefresh();
        } catch (error) {
            console.error('Refresh failed:', error);
            this.scheduleRetry();
        } finally {
            this.isRefreshing = false;
        }
    }

    private scheduleRefresh(): void {
        this.clearTimeout();
        this.log('Refresh scheduled', {delayMinutes: REFRESH_TIMEOUT / 60000});
        this.timeoutId = setTimeout(() => {
            this.refresh();
        }, REFRESH_TIMEOUT);
    }

    private scheduleRetry(): void {
        this.clearTimeout();
        this.log(`Schedule retry`, {retry_timeout_sec: RETRY_TIMEOUT / 1000});
        this.timeoutId = setTimeout(() => {
            this.refresh();
        }, RETRY_TIMEOUT);
    }

    private clearTimeout(): void {
        if (!this.timeoutId) return;

        clearTimeout(this.timeoutId);
        this.timeoutId = null;
    }

    private setupVisibilityHandler(): void {
        this.log('Setting up visibility change handler');
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                this.log('Tab became visible - validating token')
                this.refresh();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    private log(message: string, obj: Record<any, any> | null = null) {
        console.info('[TokenRefresher] ' + message, obj || '')
        addBreadcrumb('[TokenRefresher] ' + message, obj ?? {})
    }
}

export const tokenRefresher = TokenRefresher.getInstance();
